import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useResponsive } from "../hooks/useResponsive";
import { colors } from "../styles/colors";
import { PrimaryButton } from './ui/StandardButton';
import { buildLocalizedPath, translatePathToLanguage } from "../utils/routes";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const { isMobile } = useResponsive();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when screen size changes
  useEffect(() => {
    if (!isMobile && isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [isMobile, isMenuOpen]);

  // Load required namespaces; include 'header' for navigation and button translations
  const { t, i18n } = useTranslation(['header', 'hero', 'common']);
  const navigate = useNavigate();
  const location = useLocation();

  const buildLangPath = (path) => {
    return buildLocalizedPath(i18n.language, path);
  };

  const replaceLangInPath = (lng) => {
    return translatePathToLanguage(location.pathname, lng);
  };
  const navigationItems = [
    { name: t("header.nav.home"), path: "/" },
    { name: t("header.nav.about"), path: "/about" },
    { name: t("header.nav.packages"), path: "/packages" },
    { name: t("header.nav.gallery"), path: "/gallery" },
    { name: t("header.nav.faq", "FAQ"), path: "/faq" },
    { name: t("header.nav.contact"), path: "/contact" },
  ];

  return (
    <header 
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled ? "backdrop-blur-sm shadow-lg" : ""
      }`}
      style={{
        backgroundColor: isScrolled ? `${colors.secondary}F0` : 'transparent'
      }}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={buildLangPath("/")} className="flex items-center transition-colors" style={{ color: colors.text.primary }}>
            <img 
              src="/logo.png" 
              alt={t('header.logoAlt', 'Cold Experience Lapland')} 
              className="h-12 w-auto md:h-14"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={buildLangPath(item.path)}
                className="transition-colors font-medium hover:opacity-80"
                style={{ color: colors.text.primary }}
              >
                {item.name}
              </Link>
            ))}
            <PrimaryButton to={buildLangPath("/book")}>
              {t("header.bookAdventure")}
            </PrimaryButton>
            {/* Custom Language Switcher Dropdown */}
            <div className="relative ml-4">
              <button
                className="px-6 py-2 rounded-full font-semibold focus:outline-none transition-all duration-300 flex items-center"
                style={{ 
                  minWidth: '130px', 
                  height: '44px', 
                  backdropFilter: 'blur(4px)', 
                  backgroundColor: `${colors.secondary}E6`,
                  border: `1px solid ${colors.border}`,
                  color: colors.text.primary,
                  boxShadow: 'none'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = `${colors.accent.primary}CC`}
                onMouseLeave={(e) => e.target.style.backgroundColor = `${colors.secondary}E6`}
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                aria-label={t('chooseLanguage', { ns: 'hero' })}
              >
                {t(`header.languageNames.${i18n.language}`, i18n.language)}
                <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: colors.text.primary }}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>
              {showLangDropdown && (
                <div 
                  className="absolute right-0 mt-2 w-36 rounded-lg shadow-lg ring-1 ring-opacity-5 z-50"
                  style={{ backgroundColor: `${colors.secondary}E6` }}
                >
                  <button
                    className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${i18n.language === 'en' ? 'font-bold' : ''}`}
                    style={{ color: colors.text.primary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent.primary}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    onClick={() => { i18n.changeLanguage('en'); navigate(replaceLangInPath('en')); setShowLangDropdown(false); }}
                  >
                    English
                  </button>
                  <button
                    className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${i18n.language === 'de' ? 'font-bold' : ''}`}
                    style={{ color: colors.text.primary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent.primary}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    onClick={() => { i18n.changeLanguage('de'); navigate(replaceLangInPath('de')); setShowLangDropdown(false); }}
                  >
                    Deutsch
                  </button>
                  <button
                    className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${i18n.language === 'sv' ? 'font-bold' : ''}`}
                    style={{ color: colors.text.primary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent.primary}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    onClick={() => { i18n.changeLanguage('sv'); navigate(replaceLangInPath('sv')); setShowLangDropdown(false); }}
                  >
                    Svenska
                  </button>
                  <button
                    className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${i18n.language === 'pl' ? 'font-bold' : ''}`}
                    style={{ color: colors.text.primary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent.primary}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    onClick={() => { i18n.changeLanguage('pl'); navigate(replaceLangInPath('pl')); setShowLangDropdown(false); }}
                  >
                    Polski
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="transition-colors hover:opacity-80"
              style={{ color: colors.text.primary }}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div 
            className="md:hidden backdrop-blur-sm absolute top-full left-0 right-0 z-40"
            style={{ 
              backgroundColor: `${colors.secondary}F0`,
              borderTop: `1px solid ${colors.border}40`
            }}
          >
            <div className="px-4 pt-4 pb-6 space-y-3">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={buildLangPath(item.path)}
                  className="block px-4 py-3 transition-colors text-lg font-medium last:border-b-0 hover:opacity-80"
                  style={{ 
                    color: colors.text.primary,
                    borderBottom: `1px solid ${colors.border}40`
                  }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <PrimaryButton 
                to={buildLangPath("/book")}
                className="block mt-4 text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("header.bookAdventure")}
              </PrimaryButton>
              {/* Mobile Language Switcher */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <label className="block text-white text-sm font-medium mb-2">
                  {t('chooseLanguage', { ns: 'hero' })}
                </label>
                <select
                  value={i18n.language}
                  onChange={e => { 
                    const lng = e.target.value; 
                    i18n.changeLanguage(lng); 
                    navigate(replaceLangInPath(lng));
                    setIsMenuOpen(false);
                  }}
                  className="w-full px-4 py-3 rounded-lg font-medium focus:outline-none"
                  style={{
                    backgroundColor: colors.secondary,
                    border: `1px solid ${colors.border}`,
                    color: colors.text.primary
                  }}
                >
                  <option value="en" style={{ backgroundColor: colors.secondary, color: colors.text.primary }}>{t('header.languageNames.en', 'English')}</option>
                  <option value="de" style={{ backgroundColor: colors.secondary, color: colors.text.primary }}>{t('header.languageNames.de', 'Deutsch')}</option>
                  <option value="sv" style={{ backgroundColor: colors.secondary, color: colors.text.primary }}>{t('header.languageNames.sv', 'Svenska')}</option>
                  <option value="pl" style={{ backgroundColor: colors.secondary, color: colors.text.primary }}>{t('header.languageNames.pl', 'Polski')}</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
